/**
 * The time of the shopper's last chat message, kept in localStorage beside the
 * clientId. Order capture starts on a page load only while this marker is
 * recent, so a visitor who never chatted loads and sends nothing.
 *
 * The chat history cannot stand in for it: chat-rocket expires each message a
 * day after it is written, while an order placed days after the chat is the
 * one attribution most needs. The marker records only that a chat happened;
 * whether the chat influenced the order is decided in Hex, after capture.
 *
 * The bootstrap reads this on every page, so it has no imports and never throws.
 */

/**
 * Record a shopper message sent at `sentAt` (epoch ms). The stored marker only
 * moves forward, so a message delivered out of order cannot pull a recent chat
 * back and make it look old. The caller stamps each message with its own time,
 * which is what keeps an old chat from looking recent.
 *
 * Listeners hear every message, not only the ones that move the marker. Each tab
 * loads the same history into the same storage, so the first tab to write a time
 * would otherwise be the only one told about it.
 */
export function recordEngagement(appId: string, sentAt: number): void {
  const key = engagementKey(appId);
  try {
    const previous = parseStored(localStorage.getItem(key));
    if (previous === null || previous < sentAt) {
      localStorage.setItem(key, String(sentAt));
    }
  } catch {
    // Storage is blocked or full. Later pages then behave as a first visit, but
    // this page still hears about the chat below.
  }
  for (const listener of listeners) {
    try {
      listener(appId, sentAt);
    } catch {
      // One listener must not stop the others, or the caller.
    }
  }
}

const listeners = new Set<EngagementListener>();

type EngagementListener = (appId: string, sentAt: number) => void;

/**
 * Hear each shopper message recorded on this page, with its app and time, even
 * when the stored marker already holds a later time. The bootstrap uses it to
 * start order capture on the page where a shopper chats, or where their history
 * loads. Returns the unsubscribe.
 */
export function onEngagementRecorded(listener: EngagementListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The epoch-ms time of the last recorded shopper message, or null when none is stored. */
export function readLastEngagedAt(appId: string): number | null {
  try {
    return parseStored(localStorage.getItem(engagementKey(appId)));
  } catch {
    return null;
  }
}

// Named like ClientIdManager's `gladlyChat_${appId}_client_id`: the two are
// cleared together by the browser, which is what keeps the order joinable.
function engagementKey(appId: string): string {
  return `gladlyChat_${appId}_last_engaged_at`;
}

function parseStored(value: string | null): number | null {
  if (value === null) {
    return null;
  }
  const sentAt = Number(value);
  return Number.isFinite(sentAt) && sentAt > 0 ? sentAt : null;
}
